import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react'
import { supabase, DailyPrompt, DailyResponse, Duel, calculateScore, calculateSyncPercentage, getDemoPrompt } from '../lib/supabase'
import { useAuth } from './AuthContext'

export interface LeaderboardEntry {
  user_id: string
  username: string
  display_name: string
  avatar_url: string | null
  score: number
  total_plays: number
  current_streak: number
  longest_streak: number
}

interface LeaderboardData {
  players: LeaderboardEntry[]
  userRank: number | null
  userScore: number | null
  userAvgScore: number | null
  topScore: LeaderboardEntry | null
  topStreak: LeaderboardEntry | null
  topDuels: LeaderboardEntry | null
}

interface GameContextType {
  dailyPrompt: DailyPrompt | null
  dailyResponse: DailyResponse | null
  loading: boolean
  submitting: boolean
  fetchDailyPrompt: (date?: string) => Promise<void>
  submitDailyResponse: (spectrumA: number, spectrumB: number) => Promise<{ error: Error | null }>
  createDuel: (promptId: string) => Promise<{ inviteCode: string | null; error: Error | null }>
  joinDuel: (inviteCode: string) => Promise<{ error: Error | null }>
  submitDuelResponse: (duelId: string, spectrumA: number, spectrumB: number) => Promise<{ error: Error | null }>
  fetchDuel: (inviteCode: string) => Promise<void>
  currentDuel: Duel | null
  clearDuel: () => void
  duels: Duel[]
  fetchDuels: () => Promise<void>
  leaderboard: LeaderboardData | null
  fetchLeaderboard: () => Promise<void>
  submitUGC: (data: UGCSubmissionData) => Promise<{ error: Error | null }>
}

interface UGCSubmissionData {
  spectrum_a_label: string
  spectrum_a_left: string
  spectrum_a_right: string
  spectrum_b_label: string
  spectrum_b_left: string
  spectrum_b_right: string
}

const GameContext = createContext<GameContextType | null>(null)

export function GameProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuth()
  const [dailyPrompt, setDailyPrompt] = useState<DailyPrompt | null>(null)
  const [dailyResponse, setDailyResponse] = useState<DailyResponse | null>(null)
  const [currentDuel, setCurrentDuel] = useState<Duel | null>(null)
  const [duels, setDuels] = useState<Duel[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [leaderboard, setLeaderboard] = useState<LeaderboardData | null>(null)

  // Fetch today's daily prompt on mount
  useEffect(() => {
    fetchDailyPrompt()
  }, [])

  const fetchDailyPrompt = useCallback(async (date?: string) => {
    setLoading(true)
    try {
      const targetDate = date || new Date().toISOString().split('T')[0]
      
      let prompt: DailyPrompt | null = null
      
      if (supabase) {
        const { data, error } = await supabase
          .from('daily_prompts')
          .select('*')
          .eq('date', targetDate)
          .eq('is_active', true)
          .single()

        if (error && error.code !== 'PGRST116') throw error
        prompt = data
      } else {
        // Demo mode
        prompt = getDemoPrompt(targetDate)
      }
      
      setDailyPrompt(prompt)
      
      // If user is logged in, check if they already responded
      if (user && prompt && supabase) {
        const { data: response } = await supabase
          .from('daily_responses')
          .select('*')
          .eq('user_id', user.id)
          .eq('prompt_id', prompt.id)
          .single()
        
        setDailyResponse(response)
      } else {
        setDailyResponse(null)
      }
    } catch (error) {
      console.error('Error fetching daily prompt:', error)
    } finally {
      setLoading(false)
    }
  }, [user])

  const submitDailyResponse = useCallback(async (spectrumA: number, spectrumB: number) => {
    if (!user || !dailyPrompt) return { error: new Error('Not authenticated or no prompt') }
    
    setSubmitting(true)
    try {
      // Get current crowd averages
      let crowdAAvg = dailyPrompt.crowd_a_avg ?? 50
      let crowdBAvg = dailyPrompt.crowd_b_avg ?? 50
      
      if (supabase) {
        const { data: responses } = await supabase
          .from('daily_responses')
          .select('spectrum_a_value, spectrum_b_value')
          .eq('prompt_id', dailyPrompt.id)

        if (responses && responses.length > 0) {
          crowdAAvg = responses.reduce((sum, r) => sum + r.spectrum_a_value, 0) / responses.length
          crowdBAvg = responses.reduce((sum, r) => sum + r.spectrum_b_value, 0) / responses.length
        }
      }

      // Calculate scores
      const scoreA = calculateScore(spectrumA, crowdAAvg)
      const scoreB = calculateScore(spectrumB, crowdBAvg)
      const totalScore = Math.round((scoreA + scoreB) / 2)

      let response: DailyResponse | null = null
      
      if (supabase) {
        const { data, error } = await supabase
          .from('daily_responses')
          .insert({
            user_id: user.id,
            prompt_id: dailyPrompt.id,
            spectrum_a_value: spectrumA,
            spectrum_b_value: spectrumB,
            crowd_a_avg_at_submit: crowdAAvg,
            crowd_b_avg_at_submit: crowdBAvg,
            score_a: scoreA,
            score_b: scoreB,
            total_score: totalScore,
          })
          .select()
          .single()

        if (error) throw error
        response = data
      } else {
        // Demo mode
        response = {
          id: 'demo-' + Date.now(),
          user_id: user.id,
          prompt_id: dailyPrompt.id,
          spectrum_a_value: spectrumA,
          spectrum_b_value: spectrumB,
          crowd_a_avg_at_submit: crowdAAvg,
          crowd_b_avg_at_submit: crowdBAvg,
          score_a: scoreA,
          score_b: scoreB,
          total_score: totalScore,
          submitted_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
        }
      }
      
      setDailyResponse(response)
      
      // Update streak
      await updateStreak(user.id)
      
      return { error: null }
    } catch (err) {
      console.error('Error submitting response:', err)
      return { error: err as Error }
    } finally {
      setSubmitting(false)
    }
  }, [user, dailyPrompt])

  const updateStreak = async (userId: string) => {
    if (!supabase) return

    const today = new Date().toISOString().split('T')[0]
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0]

    const { data: profile } = await supabase
      .from('profiles')
      .select('current_streak, longest_streak, last_played_date, total_plays')
      .eq('id', userId)
      .single()

    if (!profile) return

    let newStreak = 1
    if (profile.last_played_date === yesterday) {
      newStreak = profile.current_streak + 1
    } else if (profile.last_played_date !== today) {
      newStreak = 1
    } else {
      newStreak = profile.current_streak
    }

    const newLongest = Math.max(profile.longest_streak, newStreak)

    await supabase
      .from('profiles')
      .update({
        current_streak: newStreak,
        longest_streak: newLongest,
        last_played_date: today,
        total_plays: (profile.total_plays || 0) + 1,
      })
      .eq('id', userId)

    // Check for freeze rewards at milestones
    if (newStreak % 7 === 0) {
      await supabase
        .from('freezes')
        .insert({ user_id: userId, source: 'streak_milestone' })
    }
  }

  const createDuel = useCallback(async (promptId: string) => {
      if (!user) return { inviteCode: null, error: new Error('Not authenticated') }
    
      try {
        const inviteCode = Math.random().toString(36).substring(2, 8).toUpperCase()
        const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()

        let duel: Duel | null = null
      
        if (supabase) {
          try {
            const { data, error } = await supabase
              .from('duels')
              .insert({
                creator_id: user.id,
                prompt_id: promptId,
                invite_code: inviteCode,
                expires_at: expiresAt,
                status: 'waiting',
              })
              .select()
              .single()

            if (error) throw error
            duel = data
          } catch (supabaseError) {
            console.warn('Supabase insert failed, falling back to demo mode:', supabaseError)
            // Fall through to demo mode
          }
        }
      
        // Demo mode fallback (if supabase is null or insert failed)
        if (!duel) {
          duel = {
            id: 'demo-duel-' + Date.now(),
            creator_id: user.id,
            prompt_id: promptId,
            invite_code: inviteCode,
            expires_at: expiresAt,
            status: 'waiting',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            creator_spectrum_a: null,
            creator_spectrum_b: null,
            opponent_spectrum_a: null,
            opponent_spectrum_b: null,
            opponent_id: null,
            crowd_a_avg: null,
            crowd_b_avg: null,
            sync_percentage: null,
            completed_at: null,
          }
        }
      
        setCurrentDuel(duel!)

        // Add the new duel to the duels list
        setDuels(prev => [duel!, ...prev])

        return { inviteCode: duel!.invite_code, error: null }
      } catch (error) {
        return { inviteCode: null, error: error as Error }
      }
    }, [user])
  const joinDuel = useCallback(async (inviteCode: string) => {
    if (!user) return { error: new Error('Not authenticated') }
    
    try {
      let duel: Duel | null = null
      
      if (supabase) {
        const { data: fetchedDuel, error: fetchError } = await supabase
          .from('duels')
          .select('*')
          .eq('invite_code', inviteCode.toUpperCase())
          .single()

        if (fetchError || !fetchedDuel) throw new Error('Invalid invite code')
        if (fetchedDuel.status !== 'waiting') throw new Error('Duel is no longer available')
        if (fetchedDuel.creator_id === user.id) throw new Error('Cannot join your own duel')
        if (fetchedDuel.opponent_id) throw new Error('Duel already has an opponent')

        const { error: updateError } = await supabase
          .from('duels')
          .update({ opponent_id: user.id, status: 'playing' })
          .eq('id', fetchedDuel.id)

        if (updateError) throw updateError

        const { data: updatedDuel } = await supabase
          .from('duels')
          .select('*')
          .eq('id', fetchedDuel.id)
          .single()

        duel = updatedDuel
      } else {
        // Demo mode
        duel = {
          id: 'demo-duel-join',
          creator_id: 'demo-creator',
          opponent_id: user.id,
          prompt_id: 'demo-1',
          invite_code: inviteCode.toUpperCase(),
          status: 'playing',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          creator_spectrum_a: null,
          creator_spectrum_b: null,
          opponent_spectrum_a: null,
          opponent_spectrum_b: null,
          crowd_a_avg: null,
          crowd_b_avg: null,
          sync_percentage: null,
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          completed_at: null,
        }
      }
      
      setCurrentDuel(duel!)
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }, [user])

  const submitDuelResponse = useCallback(async (duelId: string, spectrumA: number, spectrumB: number) => {
    if (!user || !currentDuel) return { error: new Error('Invalid state') }
    
    try {
      const isCreator = currentDuel.creator_id === user.id
      
      const updates: Record<string, number | string> = {
        [`${isCreator ? 'creator' : 'opponent'}_spectrum_a`]: spectrumA,
        [`${isCreator ? 'creator' : 'opponent'}_spectrum_b`]: spectrumB,
      }

      // If both have responded, calculate sync and complete
      const otherResponse = isCreator ? currentDuel.opponent_spectrum_a : currentDuel.creator_spectrum_a
      if (otherResponse !== null) {
        const creatorA = isCreator ? spectrumA : currentDuel.creator_spectrum_a
        const creatorB = isCreator ? spectrumB : currentDuel.creator_spectrum_b
        const opponentA = isCreator ? currentDuel.opponent_spectrum_a : spectrumA
        const opponentB = isCreator ? currentDuel.opponent_spectrum_b : spectrumB

        const syncA = calculateSyncPercentage(creatorA ?? 50, opponentA ?? 50)
        const syncB = calculateSyncPercentage(creatorB ?? 50, opponentB ?? 50)
        const syncPercentage = Math.round((syncA + syncB) / 2)

        updates.sync_percentage = syncPercentage
        updates.status = 'completed'
        updates.completed_at = new Date().toISOString()
      }

      if (supabase) {
        const { error } = await supabase
          .from('duels')
          .update(updates)
          .eq('id', duelId)

        if (error) throw error

        // Refresh duel
        const { data: updatedDuel } = await supabase
          .from('duels')
          .select('*')
          .eq('id', duelId)
          .single()

        setCurrentDuel(updatedDuel)
      } else {
        // Demo mode
        setCurrentDuel({ ...currentDuel, ...updates })
      }

      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }, [user, currentDuel])

  const fetchDuel = useCallback(async (inviteCode: string) => {
    try {
      let duel: Duel | null = null
      
      if (supabase) {
        const { data, error } = await supabase
          .from('duels')
          .select('*')
          .eq('invite_code', inviteCode.toUpperCase())
          .single()

        if (error) throw error
        duel = data
      } else {
        // Demo mode
        duel = {
          id: 'demo-duel',
          creator_id: 'demo-creator',
          opponent_id: 'demo-opponent',
          prompt_id: 'demo-1',
          invite_code: inviteCode.toUpperCase(),
          status: 'completed',
          creator_spectrum_a: 30,
          creator_spectrum_b: 70,
          opponent_spectrum_a: 35,
          opponent_spectrum_b: 65,
          crowd_a_avg: 50,
          crowd_b_avg: 50,
          sync_percentage: 85,
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          completed_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          prompt: getDemoPrompt(),
        }
      }
      
      setCurrentDuel(duel)
    } catch (error) {
      console.error('Error fetching duel:', error)
      setCurrentDuel(null)
    }
  }, [])

  const clearDuel = useCallback(() => {
    setCurrentDuel(null)
  }, [])

  const fetchDuels = useCallback(async () => {
      if (!user) {
        setDuels([])
        return
      }
    
      try {
        let fetchedDuels: Duel[] = []
      
        if (supabase) {
          try {
            const { data, error } = await supabase
              .from('duels')
              .select('*, prompt:daily_prompts(*)')
              .or(`creator_id.eq.${user.id},opponent_id.eq.${user.id}`)
              .order('created_at', { ascending: false })
          
            if (error) throw error
            fetchedDuels = data || []
          } catch (supabaseError) {
            console.warn('Supabase query failed, falling back to demo mode:', supabaseError)
            // Fall through to demo mode
          }
        }
      
        // Demo mode fallback (if supabase is null or query failed)
        if (fetchedDuels.length === 0) {
          const demoPrompt = getDemoPrompt()
          fetchedDuels = [
            {
              id: 'demo-duel-1',
              creator_id: user.id,
              opponent_id: 'demo-opponent-1',
              prompt_id: 'demo-1',
              invite_code: 'ABC123',
              status: 'waiting',
              created_at: new Date(Date.now() - 3600000).toISOString(),
              updated_at: new Date(Date.now() - 3600000).toISOString(),
              expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
              creator_spectrum_a: null,
              creator_spectrum_b: null,
              opponent_spectrum_a: null,
              opponent_spectrum_b: null,
              crowd_a_avg: null,
              crowd_b_avg: null,
              sync_percentage: null,
              completed_at: null,
              prompt: demoPrompt,
              creator_username: profile?.username || 'You',
              opponent_username: 'Friend',
            },
            {
              id: 'demo-duel-2',
              creator_id: 'demo-creator-2',
              opponent_id: user.id,
              prompt_id: 'demo-1',
              invite_code: 'XYZ789',
              status: 'playing',
              created_at: new Date(Date.now() - 7200000).toISOString(),
              updated_at: new Date(Date.now() - 7200000).toISOString(),
              expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
              creator_spectrum_a: 30,
              creator_spectrum_b: 70,
              opponent_spectrum_a: null,
              opponent_spectrum_b: null,
              crowd_a_avg: 50,
              crowd_b_avg: 50,
              sync_percentage: null,
              completed_at: null,
              prompt: demoPrompt,
              creator_username: 'Sarah',
              opponent_username: profile?.username || 'You',
            },
            {
              id: 'demo-duel-3',
              creator_id: user.id,
              opponent_id: 'demo-opponent-3',
              prompt_id: 'demo-1',
              invite_code: 'DEF456',
              status: 'completed',
              created_at: new Date(Date.now() - 86400000).toISOString(),
              updated_at: new Date(Date.now() - 86400000 + 3600000).toISOString(),
              expires_at: new Date(Date.now() - 86400000 + 24 * 60 * 60 * 1000).toISOString(),
              creator_spectrum_a: 25,
              creator_spectrum_b: 65,
              opponent_spectrum_a: 30,
              opponent_spectrum_b: 70,
              crowd_a_avg: 50,
              crowd_b_avg: 50,
              sync_percentage: 85,
              completed_at: new Date(Date.now() - 86400000 + 3600000).toISOString(),
              prompt: demoPrompt,
              creator_username: profile?.username || 'You',
              opponent_username: 'Mike',
            }
          ]
        }
      
        setDuels(fetchedDuels)
      } catch (error) {
        console.error('Error fetching duels:', error)
        setDuels([])
      }
    }, [user, profile])

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true)
    try {
      const data: LeaderboardData = {
        players: [],
        userRank: null,
        userScore: null,
        userAvgScore: null,
        topScore: null,
        topStreak: null,
        topDuels: null,
      }

      if (supabase && user) {
        // Get user's rank and score
        const { data: profile } = await supabase
          .from('profiles')
          .select('total_plays, current_streak, longest_streak')
          .eq('id', user.id)
          .single()

        if (profile) {
          // Get user's average score from daily responses
          const { data: responses } = await supabase
            .from('daily_responses')
            .select('total_score')
            .eq('user_id', user.id)

          if (responses && responses.length > 0) {
            const avgScore = Math.round(responses.reduce((sum, r) => sum + r.total_score, 0) / responses.length)
            data.userAvgScore = avgScore
          }
        }

        // Get top players
        const { data: topPlayers } = await supabase
          .from('profiles')
          .select('id, username, display_name, avatar_url, total_plays, current_streak, longest_streak')
          .order('total_plays', { ascending: false })
          .limit(100)

        if (topPlayers) {
          // Get scores for each player
          const playersWithScores = await Promise.all(
            topPlayers.map(async (p) => {
              if (supabase) {
                const { data: responses } = await supabase
                  .from('daily_responses')
                  .select('total_score')
                  .eq('user_id', p.id)

                const totalScore = responses?.reduce((sum, r) => sum + (r.total_score || 0), 0) || 0

                return {
                  user_id: p.id,
                  username: p.username,
                  display_name: p.display_name,
                  avatar_url: p.avatar_url,
                  score: totalScore,
                  total_plays: p.total_plays || 0,
                  current_streak: p.current_streak || 0,
                  longest_streak: p.longest_streak || 0,
                }
              }
              return {
                user_id: p.id,
                username: p.username,
                display_name: p.display_name,
                avatar_url: p.avatar_url,
                score: 0,
                total_plays: p.total_plays || 0,
                current_streak: p.current_streak || 0,
                longest_streak: p.longest_streak || 0,
              }
            })
          )

          data.players = playersWithScores
            .sort((a, b) => b.score - a.score)
            .map((p, i) => ({ ...p, rank: i + 1 }))

          // Find user's rank
          if (user) {
            const userIndex = data.players.findIndex(p => p.user_id === user.id)
            if (userIndex >= 0) {
              data.userRank = userIndex + 1
              data.userScore = data.players[userIndex].score
            }
          }
        }

        // Get category leaders
        if (data.players.length > 0) {
          data.topScore = data.players.reduce((max, p) => p.score > max.score ? p : max, data.players[0])
          data.topStreak = data.players.reduce((max, p) => p.current_streak > max.current_streak ? p : max, data.players[0])
          data.topDuels = data.players.reduce((max, p) => p.total_plays > max.total_plays ? p : max, data.players[0])
        }

        setLeaderboard(data)
      } else {
        // Demo mode
        const demoPlayers = [
          { user_id: '1', username: 'demo_player', display_name: 'Demo Player', avatar_url: null, score: 88, total_plays: 42, current_streak: 7, longest_streak: 14 },
          { user_id: '2', username: 'sarah_sync', display_name: 'Sarah', avatar_url: null, score: 82, total_plays: 38, current_streak: 12, longest_streak: 14 },
          { user_id: '3', username: 'mike_match', display_name: 'Mike', avatar_url: null, score: 79, total_plays: 45, current_streak: 5, longest_streak: 21 },
          { user_id: '4', username: 'emma_even', display_name: 'Emma', avatar_url: null, score: 76, total_plays: 32, current_streak: 3, longest_streak: 10 },
          { user_id: '5', username: 'alex_align', display_name: 'Alex', avatar_url: null, score: 74, total_plays: 28, current_streak: 9, longest_streak: 18 },
        ]

        const demoData: LeaderboardData = {
          players: demoPlayers.map((p, i) => ({ ...p, rank: i + 1 })),
          userRank: 1,
          userScore: 88,
          userAvgScore: 72,
          topScore: demoPlayers[0],
          topStreak: demoPlayers[2],
          topDuels: demoPlayers[0],
        }

        setLeaderboard(demoData)
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  const submitUGC = useCallback(async (data: UGCSubmissionData) => {
    if (!user) return { error: new Error('Not authenticated') }

    try {
      if (supabase) {
        const { error } = await supabase
          .from('ugc_submissions')
          .insert({
            user_id: user.id,
            spectrum_a_label: data.spectrum_a_label,
            spectrum_a_left: data.spectrum_a_left,
            spectrum_a_right: data.spectrum_a_right,
            spectrum_b_label: data.spectrum_b_label,
            spectrum_b_left: data.spectrum_b_left,
            spectrum_b_right: data.spectrum_b_right,
            status: 'pending',
          })

        if (error) throw error
      }
      // Demo mode - just simulate success
      return { error: null }
    } catch (err) {
      console.error('Error submitting UGC:', err)
      return { error: err as Error }
    }
  }, [user])

  return (
      <GameContext.Provider value={{
        dailyPrompt,
        dailyResponse,
        loading,
        submitting,
        fetchDailyPrompt,
        submitDailyResponse,
        createDuel,
        joinDuel,
        submitDuelResponse,
        fetchDuel,
        currentDuel,
        clearDuel,
        duels,
        fetchDuels,
        leaderboard,
        fetchLeaderboard,
        submitUGC,
      }}>
        {children}
      </GameContext.Provider>
    )
  }

export function useGame() {
  const context = useContext(GameContext)
  if (!context) {
    throw new Error('useGame must be used within a GameProvider')
  }
  return context
}